import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Extend dayjs with timezone support
dayjs.extend(utc)
dayjs.extend(timezone)

const PACIFIC_TIMEZONE = 'America/Los_Angeles'

/**
 * Format a date string to Pacific Time for display
 */
export const formatToPacificTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A'
    return dayjs(dateString).tz(PACIFIC_TIMEZONE).format('MMM D, YYYY h:mm A') + ' PT'
}

/**
 * Format a date string to Pacific Time, returning date and time as separate strings.
 * Useful for wrapping each part in a no-wrap span to control line breaks.
 */
export const formatPacificDateTimeParts = (dateString: string | null | undefined): { date: string; time: string } | null => {
    if (!dateString) return null
    const d = dayjs(dateString).tz(PACIFIC_TIMEZONE)
    return {
        date: d.format('MMM D, YYYY'),
        time: d.format('h:mm A') + ' PT',
    }
}

/**
 * Format a date string to venue local time for display
 */
export const formatToVenueTime = (
    dateString: string | null | undefined,
    venueTimezone: string | null | undefined
): string => {
    if (!dateString) return 'N/A'
    if (!venueTimezone) return 'Local time unavailable'

    try {
        return dayjs(dateString).tz(venueTimezone).format('MMM D, YYYY h:mm A')
    } catch (error) {
        console.error('Invalid timezone:', venueTimezone, error)
        return 'Invalid timezone'
    }
}

/**
 * Convert a datetime-local input value (in PT) to UTC ISO string for API
 */
export const convertPTInputToUTC = (dateTimeLocalValue: string): string => {
    if (!dateTimeLocalValue) return ''
    // datetime-local value is in format: YYYY-MM-DDTHH:mm
    // Treat it as Pacific Time and convert to UTC
    return dayjs.tz(dateTimeLocalValue, PACIFIC_TIMEZONE).utc().toISOString()
}

/**
 * Convert a UTC ISO string to datetime-local input value in PT
 */
export const convertUTCToPTInput = (isoString: string | null | undefined): string => {
    if (!isoString) return ''
    try {
        // Convert UTC to Pacific Time and format for datetime-local input
        return dayjs(isoString).tz(PACIFIC_TIMEZONE).format('YYYY-MM-DDTHH:mm')
    } catch {
        return ''
    }
}

/**
 * Get the timezone abbreviation for a given timezone at a specific date
 */
export const getTimezoneAbbr = (
    dateString: string | null | undefined,
    timezone: string | null | undefined
): string => {
    if (!dateString || !timezone) return ''

    try {
        const date = dayjs(dateString).tz(timezone)
        // Get timezone offset
        const offset = date.format('Z')
        return `(UTC${offset})`
    } catch {
        return ''
    }
}

